import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLingui } from '@lingui/react/macro';

import CalloutBadge from '@rover/components/src/callouts/CalloutBadge/CalloutBadge';
import CarouselRightSmall from '@rover/icons/svg/carousel-right-small.svg';
import { Box, Button, Heading, Text } from '@rover/kibble/core';
import Row from '@rover/kibble/patterns/Row';
import Section from '@rover/kibble/patterns/Section';
import RoverLoading from '@rover/react-lib/src/components/utils/RoverLoading';
import BackButtonWithFormState from '@rover/react-lib/src/pages/provider-profile/components/BackButtonWithFormState';
import { useApiCatProviderProfileRetrieve } from '@rover/rsdk/src/apiClient/latest/api-cat-provider-profile-retrieve';
import { useApiProviderProfileHubRetrieve } from '@rover/rsdk/src/apiClient/latest/api-provider-profile-hub-retrieve';
import { useApiProviderProfileModuleRetrieve } from '@rover/rsdk/src/apiClient/latest/api-provider-profile-module-retrieve';
import type { ExperienceModule } from '@rover/rsdk/src/apiClient/latest/model/experienceModule';
import type { PhotosModule } from '@rover/rsdk/src/apiClient/latest/model/photosModule';
import type { RetrieveCatProviderProfile } from '@rover/rsdk/src/apiClient/latest/model/retrieveCatProviderProfile';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import { useProviderPetTypes } from '@rover/shared/js/hooks/useProviderPetTypes';
import { redirect } from '@rover/utilities/url';

import ProfileSummaryPreview from '../components/ProfileSummaryPreview';
import { MODULE_KEYS } from '../constants/moduleKeys';
import { useHubReturnPath } from '../hooks/useHubReturnPath';
import { useProfileRoutes } from '../hooks/useProfileRoutes';
import { useShouldShowNewManagementHub } from '../hooks/useShouldShowNewManagementHub';

const SitterProfileContent: React.FC = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const routes = useProfileRoutes();
  const shouldShowNewManagementHub = useShouldShowNewManagementHub();
  const isEpsu = routes.details.includes('/existing-provider-sign-up/');
  const hubReturnPath = useHubReturnPath();
  const parentHubUrl =
    shouldShowNewManagementHub && !isEpsu
      ? hubReturnPath
      : routes.details.replace(/\/details\/$/, '/');

  // Fetch current user data
  const { data: currentUser } = useCurrentUser();
  const userName =
    currentUser && 'currentUser' in currentUser
      ? `${currentUser.currentUser.firstName} ${currentUser.currentUser.lastName}`.trim()
      : '';

  // Fetch hub data to check if is_management
  const { data: hubData, isLoading: hubLoading } = useApiProviderProfileHubRetrieve();
  const isManagement = hubData?.hub?.slug === 'management';

  // Fetch dog profile data (experience module)
  const { data: experienceData, isLoading: experienceLoading } =
    useApiProviderProfileModuleRetrieve(MODULE_KEYS.EXPERIENCE);
  const experienceModule = experienceData?.data as ExperienceModule | undefined;

  // Fetch photos module for dog profile image
  const { data: photosData, isLoading: photosLoading } = useApiProviderProfileModuleRetrieve(
    MODULE_KEYS.PHOTOS_V2
  );
  const photosModule = photosData?.data as PhotosModule | undefined;

  // Fetch cat profile data
  const { data: catProfileData, isLoading: catProfileLoading } = useApiCatProviderProfileRetrieve();
  const catProfile = catProfileData as RetrieveCatProviderProfile | undefined;

  // Check which pet types the provider offers
  const { offersCatCare, offersDogCare, isLoading: petTypesLoading } = useProviderPetTypes();

  const dogSkillsNotSaved =
    experienceModule?.firstAidCpr === null ||
    experienceModule?.canProvideOralMedication === null ||
    experienceModule?.canProvideInjectedMedication === null ||
    experienceModule?.seniorDogCareExperience === null ||
    experienceModule?.specialNeedsDogCareExperience === null ||
    experienceModule?.canProvideDailyExercise === null;

  const catSkillsNotSaved =
    catProfile?.canProvideOralMedication === null ||
    catProfile?.canProvideInjectedMedication === null;

  const skillsNotSaved =
    (offersDogCare && dogSkillsNotSaved) || (offersCatCare && catSkillsNotSaved);

  const catExperienceNotCompleted =
    offersCatCare &&
    (!catProfile?.headline || !catProfile?.careExperience || !catProfile?.yearsOfExperience);

  const whatToExpectNotSaved =
    experienceModule?.availability === '' || experienceModule?.environment === '';

  const isLoading =
    hubLoading || experienceLoading || photosLoading || catProfileLoading || petTypesLoading;

  if (isLoading) {
    return (
      <Section maxWidth={['100%', '640px']} data-qa-id="sitter-profile-page" py="6x">
        <RoverLoading isLoading>
          <Box minHeight="400px" />
        </RoverLoading>
      </Section>
    );
  }

  return (
    <Section maxWidth={['100%', '640px']} data-qa-id="sitter-profile-page" py="6x">
      <BackButtonWithFormState href={parentHubUrl} />

      <Box py="8x">
        <Heading size="400" mb="3x">
          {isManagement ? t`Sitter Profile` : t`Build your profile`}
        </Heading>

        <Text textColor="primary" size="200" marginBottom="6x">
          {t`This will be shown to pet owners in search and on your profile.`}
        </Text>
      </Box>

      {offersDogCare || experienceModule?.headline ? (
        <Box mb="6x">
          <Row
            label={t`Experience`}
            sublabel={t`Highlight your experience to connect with clients.`}
            rightItem={<CarouselRightSmall />}
            role="button"
            ariaLabel={t`Edit profile details`}
            onClick={() => navigate(routes.dogCareDetails)}
          />

          <ProfileSummaryPreview
            profileImage={photosModule?.profilePhoto?.bigThumb}
            sitterName={userName}
            headline={experienceModule?.headline}
            onClick={() => navigate(routes.dogCareDetails)}
          />
        </Box>
      ) : null}

      {offersCatCare || catProfile?.headline ? (
        <Box mb="6x">
          <Row
            label={
              <>
                <Text size="200" fontWeight="semibold" mr="2x">
                  {t`Cat experience`}
                </Text>
                {catExperienceNotCompleted && (
                  <CalloutBadge
                    value={t`MORE`}
                    styleOverrides={{
                      height: 16,
                      'line-height': '1.1',
                    }}
                  />
                )}
              </>
            }
            sublabel={t`Customize your experience to connect with cat clients.`}
            rightItem={<CarouselRightSmall />}
            role="button"
            ariaLabel={t`Edit cat profile details`}
            onClick={() => navigate(routes.catCareDetails)}
          />

          <ProfileSummaryPreview
            profileImage={catProfile?.profilePhoto?.bigThumb}
            sitterName={userName}
            headline={catProfile?.headline}
            onClick={() => navigate(routes.catCareDetails)}
          />
        </Box>
      ) : null}

      {isManagement && (
        <Box mb="6x">
          <Row
            label={
              <>
                <Text size="200" fontWeight="semibold" mr="2x">
                  {t`Skills`}
                </Text>
                {skillsNotSaved && (
                  <CalloutBadge
                    value={t`MORE`}
                    styleOverrides={{
                      height: 16,
                      'line-height': '1.1',
                    }}
                  />
                )}
              </>
            }
            sublabel={t`Select which special skills you can provide.`}
            rightItem={<CarouselRightSmall />}
            role="button"
            ariaLabel={t`Edit skills`}
            onClick={() => navigate(routes.skills)}
          />
        </Box>
      )}

      <Box mb="10x">
        <Row
          label={
            <>
              <Text size="200" fontWeight="semibold" mr="2x">
                {t`What to Expect`}
              </Text>
              {whatToExpectNotSaved && (
                <CalloutBadge
                  value={t`MORE`}
                  styleOverrides={{
                    height: 16,
                    'line-height': '1.1',
                  }}
                />
              )}
            </>
          }
          sublabel={t`Describe what a typical day looks like, your environment, and other important info.`}
          rightItem={<CarouselRightSmall />}
          role="button"
          ariaLabel={t`Edit what to expect`}
          onClick={() => navigate(routes.whatToExpect)}
        />
      </Box>

      <Box mb="4x">
        <Button
          variant="primary"
          onClick={() => {
            redirect(parentHubUrl);
          }}
          fullWidth
          data-qa-id="save-button"
        >
          {t`Save`}
        </Button>
      </Box>
    </Section>
  );
};

export default SitterProfileContent;
